import React, { useEffect, useRef } from "react";

import { Controller, useFormContext } from "react-hook-form";

import { AgentModelSelector } from "src/agents/AgentVersionPage/AgentModelSelector";
import { AgentSchemaEditor } from "src/agents/AgentVersionPage/AgentSchemaEditor";
import { AgentToolsEditor } from "src/agents/AgentVersionPage/AgentToolsEditor";
import {
  ConfigSection,
  useAgentVersionConfigPanel,
} from "src/agents/AgentVersionPage/AgentVersionConfigPanelContext";
import { useAgentVersionFormContext } from "src/agents/AgentVersionPage/AgentVersionFormProvider";
import { ExecutionSettingsEditor } from "src/agents/AgentVersionPage/ExecutionSettingsEditor";
import { AgentVersion, AgentVersionConfigurationInput } from "src/agents/types";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Switch } from "src/base-components/Switch";
import { Textarea } from "src/base-components/Textarea";
import { Accordion } from "src/design-system/Accordion";
import { WhitePane } from "src/jobs/common/WhitePane";
import { useScrollIntoView } from "src/utils/useScrollIntoView";

type AgentVersionConfigurationFormProps = {
  version: AgentVersion;
};

// IMPORTANT! Changes to the layout of the form must be reflected in the skeleton placeholder below.
export const AgentVersionConfigPanel: React.FC<
  AgentVersionConfigurationFormProps
> = ({ version }) => {
  const { isLoading } = useAgentVersionFormContext();

  // Show skeleton while loading
  if (isLoading) {
    return <AgentVersionConfigPanelSkeleton />;
  }

  return <AgentVersionConfigPanelContent version={version} />;
};

/**
 * Main form that contains all the agent config options.
 * Uses FormProvider from AgentVersionFormProvider (via useFormContext).
 */
const AgentVersionConfigPanelContent: React.FC<
  AgentVersionConfigurationFormProps
> = ({ version }) => {
  const formMethods = useFormContext<AgentVersionConfigurationInput>();
  const { modelOptions } = useAgentVersionFormContext();

  const {
    accordionValues,
    setAccordionValues,
    registerScrollFn,
    scrollToSection,
  } = useAgentVersionConfigPanel();

  // Scroll hooks for each accordion section
  // Note: Schema scroll is registered by AgentSchemaEditor to scroll to a lower position
  const { ref: modelPromptRef, scrollIntoView: scrollModelPrompt } =
    useScrollIntoView();
  const { ref: baseSettingsRef, scrollIntoView: scrollBaseSettings } =
    useScrollIntoView();
  const { ref: toolsRef, scrollIntoView: scrollTools } = useScrollIntoView();
  const { ref: experimentalRef, scrollIntoView: scrollExperimental } =
    useScrollIntoView();

  // Register scroll functions with context
  useEffect(() => {
    registerScrollFn(ConfigSection.ModelPrompt, scrollModelPrompt);
    registerScrollFn(ConfigSection.BaseSettings, scrollBaseSettings);
    registerScrollFn(ConfigSection.Tools, scrollTools);
    // Schema section scroll function is registered by AgentSchemaEditor
    registerScrollFn(ConfigSection.Experimental, scrollExperimental);
  }, [
    registerScrollFn,
    scrollModelPrompt,
    scrollBaseSettings,
    scrollTools,
    scrollExperimental,
  ]);

  // Track previous accordion values to detect newly expanded sections
  const prevAccordionValuesRef = useRef<string[]>([ConfigSection.ModelPrompt]);

  const isPublished = version.status === "published";

  return (
    <div className="h-auto">
      <WhitePane noMinWidth>
        <div className="h-full w-full px-5 py-4">
          <div className="mb-6 border-gray-200 border-b pb-4">
            <h2 className="font-inter-semibold-13px text-gray-800">
              Configure Agent
            </h2>
            <p className="mt-1 font-inter-normal-12px text-gray-500">
              Define how your agent behaves and executes actions.
            </p>
          </div>
          <Accordion
            value={accordionValues}
            onChange={(values) => {
              // Find newly expanded section (value that wasn't in previous state)
              const newlyExpanded = values.find(
                (v) => !prevAccordionValuesRef.current.includes(v),
              );
              prevAccordionValuesRef.current = values;
              setAccordionValues(values);

              // Only scroll if a section was expanded, not collapsed
              // Delay to wait for accordion animation to complete
              if (newlyExpanded) {
                setTimeout(
                  () => scrollToSection(newlyExpanded as ConfigSection),
                  100,
                );
              }
            }}
          >
            <div ref={modelPromptRef}>
              <Accordion.Item
                dataLoc="model-prompt-accordion"
                title="Model & Prompt"
                value={ConfigSection.ModelPrompt}
              >
                <div className="rounded-lg bg-gray-50 px-4 py-3">
                  <div className="mb-6">
                    <label className="mb-1 inline-block font-inter-semibold-13px text-gray-800">
                      Model
                    </label>
                    <p className="mb-2 font-inter-normal-12px text-gray-500">
                      Pick an AI model for your Agent. Each model has different
                      capabilities, speed, and cost characteristics.
                    </p>
                    <div className="w-full max-w-[440px]">
                      <AgentModelSelector
                        disabled={isPublished}
                        modelOptions={modelOptions}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 inline-block font-inter-semibold-13px text-gray-800">
                      Prompt
                    </label>
                    <div className="h-90">
                      <Controller
                        control={formMethods.control}
                        name="model_config.system_prompt"
                        render={({ field }) => (
                          <Textarea
                            disabled={isPublished}
                            placeholder="You are a helpful AI assistant"
                            value={field.value || ""}
                            fullHeight
                            fullWidth
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </Accordion.Item>
            </div>
            {/* relative wrapper so the absolute "Add tool" button inside AgentToolsEditor
                escapes the accordion panel's overflow-hidden */}
            <div ref={toolsRef} className="relative">
              <Accordion.Item
                dataLoc="tools-accordion"
                title="Tools"
                value={ConfigSection.Tools}
              >
                <AgentToolsEditor immutable={isPublished} />
              </Accordion.Item>
            </div>
            <div>
              {/* forceMount keeps tab state (Input/Output) preserved when accordion is collapsed */}
              <Accordion.Item
                dataLoc="schema-accordion"
                title="Schema"
                value={ConfigSection.Schema}
                forceMount
              >
                <AgentSchemaEditor immutable={isPublished} />
              </Accordion.Item>
            </div>
            <div ref={baseSettingsRef}>
              <Accordion.Item
                dataLoc="advanced-settings-accordion"
                title="Advanced settings"
                value={ConfigSection.BaseSettings}
              >
                <ExecutionSettingsEditor immutable={isPublished} />
              </Accordion.Item>
            </div>
            <div ref={experimentalRef}>
              <Accordion.Item
                dataLoc="experimental-accordion"
                title="Experimental"
                value={ConfigSection.Experimental}
              >
                <div className="flex flex-col gap-4 rounded-lg bg-gray-50 px-4 py-3">
                  <Controller
                    control={formMethods.control}
                    name="experimental.tag_untrusted_content"
                    render={({ field }) => (
                      <div className="flex items-start justify-between gap-x-2">
                        <div>
                          <h3 className="font-inter-semibold-13px text-gray-800">
                            Tag untrusted content
                          </h3>
                          <span className="font-inter-normal-12px text-gray-500">
                            Helps detect untrusted content from users and
                            external sources to prevent malicious actors from
                            influencing model behaviour.
                          </span>
                        </div>
                        <Switch
                          dataLoc="tag-untrusted-content-toggle"
                          disabled={isPublished}
                          enabled={field.value ?? false}
                          onChange={field.onChange}
                        />
                      </div>
                    )}
                  />
                </div>
              </Accordion.Item>
            </div>
          </Accordion>
        </div>
      </WhitePane>
    </div>
  );
};

export const AgentVersionConfigPanelSkeleton: React.FC = () => {
  return (
    <WhitePane noMinWidth>
      <div className="h-full w-full px-5 py-4">
        <div className="mb-6 border-gray-100 border-b pb-4">
          <h2 className="font-inter-semibold-13px text-gray-800">
            Configure Agent
          </h2>
          <p className="mt-1 font-inter-normal-12px text-gray-500">
            Define how your agent behaves and executes actions.
          </p>
        </div>
        <div className="divide-y divide-gray-200 rounded-lg border border-gray-200">
          <div className="border-gray-200 border-b">
            <div className="flex h-13 items-center px-4">
              <SkeletonPlaceholder height="h-5" width="w-32" />
            </div>
            <div className="pt-2 pr-5 pb-7 pl-7">
              <div className="rounded-lg bg-gray-50 px-4 py-3">
                <div className="mb-6">
                  <SkeletonPlaceholder height="h-4" width="w-16" />
                  <div className="mt-1 mb-2">
                    <SkeletonPlaceholder height="h-4" width="w-80" />
                  </div>
                  <div className="w-full max-w-[440px]">
                    <SkeletonPlaceholder height="h-10" width="w-full" />
                  </div>
                </div>
                <div>
                  <SkeletonPlaceholder height="h-4" width="w-16" />
                  <div className="mt-2 h-90">
                    <SkeletonPlaceholder height="h-full" width="w-full" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex h-13 items-center border-gray-200 border-b px-4">
            <SkeletonPlaceholder height="h-5" width="w-48" />
          </div>
          <div className="flex h-13 items-center border-gray-200 border-b px-4">
            <SkeletonPlaceholder height="h-5" width="w-24" />
          </div>
          <div className="flex h-13 items-center border-gray-200 border-b px-4">
            <SkeletonPlaceholder height="h-5" width="w-28" />
          </div>
          <div className="flex h-13 items-center px-4">
            <SkeletonPlaceholder height="h-5" width="w-32" />
          </div>
        </div>
      </div>
    </WhitePane>
  );
};
